import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Plus, Pencil, Tag, Trash2, Lock, Store } from 'lucide-react';
import { PageHeader } from '@/components/common/PageHeader';
import { DataTable, type Column } from '@/components/common/DataTable';
import { ErrorState } from '@/components/common/ErrorState';
import { Toolbar } from '@/components/common/Toolbar';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { categoriesService } from '@/services/inventory';
import type { Category } from '@/types/inventory';
import { useDebounced } from '@/hooks/useDebounced';
import { CategoryFormSheet } from './CategoryFormSheet';

type KindFilter = 'ALL' | 'SELLABLE' | 'INTERNAL';

export function CategoriesPage() {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [kindFilter, setKindFilter] = useState<KindFilter>('ALL');
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<Category | null>(null);
  const [confirmRemove, setConfirmRemove] = useState<Category | null>(null);
  const debounced = useDebounced(search);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['inventory', 'categories'],
    queryFn: () => categoriesService.list(),
  });

  // L3 — soft-remove category. BE refuses when items are still linked
  // or subcategories are nested; the error message comes back verbatim
  // (e.g. "Cannot remove — 4 items still in this category. Re-assign…").
  const remove = useMutation({
    mutationFn: (id: string) => categoriesService.remove(id),
    onSuccess: () => {
      toast.success('Category removed');
      qc.invalidateQueries({ queryKey: ['inventory', 'categories'] });
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : 'Could not remove category'),
  });

  const onRemove = (c: Category) => setConfirmRemove(c);

  const byId = useMemo(() => {
    const map = new Map<string, Category>();
    (data ?? []).forEach((c) => map.set(c.id, c));
    return map;
  }, [data]);

  // Treat a missing categoryKind as SELLABLE (legacy rows / pre-ADR-040 bundle).
  const kindOf = (c: Category): 'SELLABLE' | 'INTERNAL' =>
    c.categoryKind === 'INTERNAL' ? 'INTERNAL' : 'SELLABLE';

  const counts = useMemo(() => {
    const rows = data ?? [];
    return {
      ALL: rows.length,
      SELLABLE: rows.filter((c) => kindOf(c) === 'SELLABLE').length,
      INTERNAL: rows.filter((c) => kindOf(c) === 'INTERNAL').length,
    };
  }, [data]);

  const filtered = useMemo(() => {
    let rows = data ?? [];
    if (kindFilter !== 'ALL') rows = rows.filter((c) => kindOf(c) === kindFilter);
    const q = debounced.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (c) =>
        c.code.toLowerCase().includes(q) ||
        c.name.toLowerCase().includes(q) ||
        (c.description?.toLowerCase().includes(q) ?? false),
    );
  }, [data, debounced, kindFilter]);

  const columns: Column<Category>[] = [
    {
      key: 'code',
      header: 'Code',
      cell: (c) => <span className="font-mono text-xs text-foreground">{c.code}</span>,
    },
    {
      key: 'name',
      header: 'Name',
      cell: (c) => (
        <div className="space-y-0.5">
          <div className="font-medium text-foreground">{c.name}</div>
          {c.description && (
            <div className="line-clamp-1 text-xs text-muted-foreground">{c.description}</div>
          )}
        </div>
      ),
    },
    {
      key: 'parent',
      header: 'Parent',
      cell: (c) =>
        c.parentId ? (
          <span className="text-sm">
            <span className="font-mono text-xs text-muted-foreground">
              {byId.get(c.parentId)?.code ?? '—'}
            </span>{' '}
            {byId.get(c.parentId)?.name ?? <span className="text-muted-foreground">(unknown)</span>}
          </span>
        ) : (
          <span className="text-xs text-muted-foreground">Top-level</span>
        ),
    },
    {
      key: 'attributes',
      header: 'Attributes',
      cell: (c) => {
        const n = c.attributeTemplate?.itemAttributes?.length ?? 0;
        return n > 0 ? (
          <span className="text-sm text-foreground">{n}</span>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        );
      },
    },
    {
      key: 'visibility',
      header: 'Visibility',
      cell: (c) =>
        kindOf(c) === 'INTERNAL' ? (
          <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700 ring-1 ring-inset ring-amber-200">
            <Lock className="h-3 w-3" /> Internal
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700 ring-1 ring-inset ring-emerald-200">
            <Store className="h-3 w-3" /> Storefront
          </span>
        ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      cell: (c) => (
        <div className="-mr-2 flex justify-end gap-0.5">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setEditing(c)}
            aria-label={`Edit ${c.name}`}
          >
            <Pencil className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onRemove(c)}
            disabled={remove.isPending}
            aria-label={`Remove ${c.name}`}
            className="text-muted-foreground hover:text-destructive"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Inventory"
        title="Categories"
        description="Organisational tags for inventory items. Pick a category in the item form; categories do not affect stock or pricing."
        actions={
          <Button size="sm" onClick={() => setCreateOpen(true)}>
            <Plus className="h-3.5 w-3.5" />
            New category
          </Button>
        }
      />

      <div className="flex items-center gap-1 border-b">
        {(
          [
            { k: 'ALL', label: 'All' },
            { k: 'SELLABLE', label: 'Storefront' },
            { k: 'INTERNAL', label: 'Internal' },
          ] as const
        ).map((t) => {
          const active = kindFilter === t.k;
          return (
            <button
              key={t.k}
              type="button"
              onClick={() => setKindFilter(t.k)}
              className={
                '-mb-px border-b-2 px-3 py-2 text-[13px] font-medium transition-colors ' +
                (active
                  ? 'border-primary text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground')
              }
            >
              {t.label}
              <span className="ml-1.5 text-[11px] text-muted-foreground">{counts[t.k]}</span>
            </button>
          );
        })}
      </div>

      <Toolbar
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search code, name, description…"
      />

      {error ? (
        <ErrorState error={error} onRetry={() => refetch()} />
      ) : (
        <DataTable
          columns={columns}
          rows={filtered}
          rowKey={(c) => c.id}
          isLoading={isLoading}
          emptyTitle="No categories yet"
          emptyDescription="Create the first category to start organising your inventory."
          emptyAction={
            <Button size="sm" onClick={() => setCreateOpen(true)}>
              <Tag className="h-3.5 w-3.5" />
              New category
            </Button>
          }
        />
      )}

      <CategoryFormSheet open={createOpen} onOpenChange={setCreateOpen} />
      <CategoryFormSheet
        open={!!editing}
        onOpenChange={(o) => !o && setEditing(null)}
        category={editing}
      />
      <ConfirmDialog
        open={!!confirmRemove}
        onOpenChange={(o) => !o && setConfirmRemove(null)}
        title={confirmRemove ? `Remove category "${confirmRemove.name}"?` : ''}
        description={
          <>
            It will be archived. Existing items already in this category stay
            linked, but new items and the portal picker won't see it. You can
            restore it later from the catalog list.
          </>
        }
        confirmLabel="Remove category"
        onConfirm={() => {
          if (confirmRemove) void remove.mutateAsync(confirmRemove.id);
        }}
      />
    </div>
  );
}
